import type { ReactNode } from 'react'

import {
  Card,
  DataField,
  Header,
  IconQuestion,
  IconShow,
  SecondaryButton,
  Space,
  StatusBadge,
  SubText,
  Text,
  WithPopover,
  WithTooltip,
} from '@procurify/ui'

import { CostSummary } from '@webapp/core/components/CostSummary'
import { Price } from '@webapp/core/components/Price'
import { useProcurifyIntl } from '@webapp/hooks'
import { FinancialAccountSummaryLoader } from './components/FinancialAccountSummaryLoader/FinancialAccountSummaryLoader'
import messages from './messages'
import { FinancialAccountSummaryCardStyled } from './styles/FinancialAccountSummaryCardStyled'
import { useFinancialAccountSummaryCard } from './useFinancialAccountSummaryCard'

export interface IFinancialAccountSummaryCardProps {
  actions?: ReactNode
}

export const FinancialAccountSummaryCard = ({
  actions,
}: IFinancialAccountSummaryCardProps) => {
  const intl = useProcurifyIntl()

  const {
    data: {
      currency,
      icon,
      accountBalance,
      allocatedFunds,
      allocatedToBillPayments,
      allocatedToSpendingCards,
      availableFunds,
      availableFundsTooltipText,
      balanceSummaryUpdatedTimeText,
      flagLabel,
    },
    commands: { revealBalanceSummary },
    states: {
      showBalanceSummary,
      isBalanceSummaryUnavailable,
      showBalanceSummaryUpdatedTime,
      showFundsLabelIcons,
      isAllocatedFundsPopoverDisabled,
      showInsufficientFundsLabel,
      showLoader,
      showInactiveAccountLabel,
      showBillPaymentsAllocatedFunds,
      showSpendingCardsAllocatedFunds,
      showShowBalanceButton,
      isShowBalanceButtonLoading,
    },
  } = useFinancialAccountSummaryCard()

  const renderAmount = (price: number | string) => {
    if (!showBalanceSummary) return <Header as='h5'>••••••••</Header>
    if (isBalanceSummaryUnavailable)
      return (
        <WithTooltip
          title={intl.formatMessage(messages.unavailableAmountTooltipText)}
          placement='top'
        >
          <WithTooltip.Wrapper tabIndex={0}>
            <Text color='textTertiary' italic inline>
              {intl.formatMessage(messages.unavailableAmountText)}
            </Text>
          </WithTooltip.Wrapper>
        </WithTooltip>
      )
    return (
      <Header as='h5'>
        <Price price={price} currency={currency} />
      </Header>
    )
  }

  const renderBalanceUpdatedTime = () => (
    <SubText color='textSecondary'>
      {balanceSummaryUpdatedTimeText}
      <WithTooltip
        title={intl.formatMessage(messages.outdatedBalanceTooltipText)}
        placement='top'
      >
        <WithTooltip.Wrapper tabIndex={0}>
          <IconQuestion
            ml={0.25}
            size={12}
            ariaLabel={intl.formatMessage(messages.outdatedBalanceTooltipLabel)}
          />
        </WithTooltip.Wrapper>
      </WithTooltip>
    </SubText>
  )

  return (
    <FinancialAccountSummaryCardStyled>
      <Card>
        <Card.Content>
          {showLoader ? (
            <FinancialAccountSummaryLoader />
          ) : (
            <Space
              direction='horizontal'
              align='center'
              justify='space-between'
            >
              <Space direction='horizontal' size='xlarge'>
                <Space direction='horizontal' align='center'>
                  <img src={icon} alt={flagLabel} height={32} />

                  <DataField
                    label={intl.formatMessage(messages.accountBalanceLabel)}
                  >
                    {showInactiveAccountLabel ? (
                      <StatusBadge
                        type='warning'
                        text={intl.formatMessage(messages.inactiveAccountLabel)}
                      />
                    ) : (
                      <Space size='xsmall'>
                        <Space direction='horizontal' size='small' align='end'>
                          {renderAmount(accountBalance)}
                          {showInsufficientFundsLabel && (
                            <StatusBadge
                              text={intl.formatMessage(
                                messages.insufficientFundsLabel
                              )}
                              type='warning'
                            />
                          )}
                        </Space>
                        {showBalanceSummaryUpdatedTime &&
                          renderBalanceUpdatedTime()}
                      </Space>
                    )}
                  </DataField>
                </Space>

                <Space.Divider />

                <Space size='xsmall'>
                  <WithPopover
                    isDisabled={isAllocatedFundsPopoverDisabled}
                    content={
                      <Card.Content>
                        <CostSummary>
                          <CostSummary.Cost>
                            {showSpendingCardsAllocatedFunds && (
                              <>
                                <th>
                                  {intl.formatMessage(
                                    messages.spendingCardsLabel
                                  )}
                                </th>
                                <td>
                                  <Price
                                    currency={currency}
                                    price={allocatedToSpendingCards}
                                  />
                                </td>
                              </>
                            )}

                            {showBillPaymentsAllocatedFunds && (
                              <>
                                <th>
                                  {intl.formatMessage(
                                    messages.upcomingPaymentsLabel
                                  )}
                                </th>
                                <td>
                                  <Price
                                    currency={currency}
                                    price={allocatedToBillPayments}
                                  />
                                </td>
                              </>
                            )}
                          </CostSummary.Cost>
                        </CostSummary>
                      </Card.Content>
                    }
                    placement='top'
                  >
                    <DataField
                      label={intl.formatMessage(messages.allocatedFundsLabel)}
                      labelIcon={
                        showFundsLabelIcons ? <IconQuestion /> : undefined
                      }
                    >
                      {renderAmount(allocatedFunds)}
                    </DataField>
                  </WithPopover>
                  {showBalanceSummaryUpdatedTime && renderBalanceUpdatedTime()}
                </Space>

                <Space.Divider />

                <DataField
                  label={intl.formatMessage(messages.availableFundsLabel)}
                  labelTooltipMessage={availableFundsTooltipText}
                  labelTooltipAriaLabel={intl.formatMessage(
                    messages.availableFundsTooltipLabel
                  )}
                  labelIcon={showFundsLabelIcons ? <IconQuestion /> : undefined}
                >
                  <Space size='xsmall'>
                    {renderAmount(availableFunds)}
                    {showBalanceSummaryUpdatedTime &&
                      renderBalanceUpdatedTime()}
                  </Space>
                </DataField>
              </Space>

              <Space direction='horizontal'>
                {showShowBalanceButton && (
                  <SecondaryButton
                    iconComponent={<IconShow />}
                    onClick={revealBalanceSummary}
                    disabled={isShowBalanceButtonLoading}
                  >
                    {intl.formatMessage(messages.showBalanceButtonText)}
                  </SecondaryButton>
                )}

                {actions}
              </Space>
            </Space>
          )}
        </Card.Content>
      </Card>
    </FinancialAccountSummaryCardStyled>
  )
}
